import { Box, Slider, styled, Typography, useTheme } from '@mui/material';
import { vec2 } from 'gl-matrix';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import useDose from '~/hooks/patient/dose/useDose';
import { doseScaleState } from '~/state/atoms/dose/doseAtoms';
import { OverlayLabel, OverlayRow } from './OverlayLabel';

export const calculateRelativeDoseInPercent = (
    maxDoseGy?: number,
    normalizationValueGy?: number
): number | undefined => {
    if (!maxDoseGy || !normalizationValueGy) return undefined;
    return (100 * maxDoseGy) / normalizationValueGy;
};

export const calculateColorwashStep = (
    relativeDoseInPercent?: number
): number => {
    return relativeDoseInPercent ? 1 / relativeDoseInPercent : 0.01;
};

export const generateDoseLevelLabel = (
    dose: { maxDoseGy: number; generated: boolean } | undefined,
    doseScale: number,
    relativeDoseInPercent?: number
): string | null => {
    if (!dose || !relativeDoseInPercent) return null;

    const maxDosePercentage = relativeDoseInPercent.toFixed(0);
    const maxDoseGy = dose.maxDoseGy.toFixed(1);
    const doseRescaledByValue =
        doseScale !== 1 ? (((doseScale - 1) / 1) * 100).toFixed() : null;

    return dose.generated &&
        doseRescaledByValue &&
        parseFloat(doseRescaledByValue) !== 0
        ? `Max: ${maxDosePercentage} % (${maxDoseGy} Gy, scaled by ${doseRescaledByValue} %)`
        : `Max: ${maxDosePercentage} % (${maxDoseGy} Gy)`;
};

export const relativeToAbsoluteDose = (
    value: number,
    maxDose: number
): string => (value * maxDose).toFixed(1);

const ColorwashSlider = styled(Slider)(({ theme }) => ({
    height: 200,
    width: 16,
    boxSizing: 'border-box',
    pointerEvents: 'auto',
    '& .MuiSlider-thumb': {
        border: '1px solid #fff',
        backgroundColor: `${theme.palette.primary.main}`,
        width: 20,
        height: 8,
        borderRadius: 0,
    },
    '& .MuiSlider-valueLabel': {
        backgroundColor: 'transparent',
        padding: 0,
        left: '46px',
        top: '12px',
    },
    '& .MuiSlider-mark': {
        background: 'transparent',
    },
    '& .MuiSlider-rail': {
        height: '100%',
        background:
            'linear-gradient(to bottom, red, orange, yellow, lightgreen, lightblue, blue)',
        opacity: 0.7,
        borderRadius: 0,
    },
    '& .MuiSlider-track': {
        background: 'transparent',
        border: `3px solid ${theme.palette.background.paper}`,
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
    },
}));

export const DoseColorwashSlider = () => {
    const theme = useTheme();
    const { dose, setDoseRange } = useDose();
    const doseRange = dose?.selectedRange;
    const doseScale = useRecoilValue(doseScaleState);

    const relativeDoseInPercent = useMemo(
        () =>
            calculateRelativeDoseInPercent(
                dose?.maxDoseGy,
                dose?.normalizationValueGy
            ),
        [dose]
    );

    const colorwashStep = useMemo(
        () => calculateColorwashStep(relativeDoseInPercent),
        [relativeDoseInPercent]
    );

    const doseLevelLabel = useMemo(
        () => generateDoseLevelLabel(dose, doseScale, relativeDoseInPercent),
        [dose, relativeDoseInPercent, doseScale]
    );

    const onColorwashValueChange = (
        _e: Event,
        value: number[] | number
    ): void => {
        const [min, max] = value as number[];
        if (doseRange && max !== min) {
            setDoseRange(vec2.fromValues(min, max));
        }
    };

    return (
        doseRange && (
            <Box display="grid" gap={2}>
                <OverlayRow ml={-1} flexDirection="column">
                    <OverlayLabel>{doseLevelLabel}</OverlayLabel>
                    {dose?.maxDoseGy.toFixed(1) !==
                        dose?.normalizationValueGy.toFixed(1) && (
                        <OverlayLabel>
                            Normalized to 100%:{' '}
                            {dose?.normalizationValueGy.toFixed(1)} Gy
                        </OverlayLabel>
                    )}
                </OverlayRow>
                <ColorwashSlider
                    orientation="vertical"
                    value={[doseRange[0] || 0, doseRange[1] || 0]}
                    step={colorwashStep}
                    min={0}
                    max={1}
                    size="small"
                    onChange={onColorwashValueChange}
                    valueLabelDisplay="on"
                    data-testid="dose-colorwash-slider"
                    valueLabelFormat={(value) => (
                        <Box
                            p={0.5}
                            bgcolor={'rgba(0, 0, 0, 0.4)'}
                            borderRadius={1}
                            display="flex"
                            flexDirection="column"
                            gap={theme.spacing(0.25)}
                        >
                            <OverlayLabel fontSize={20}>
                                {(relativeDoseInPercent
                                    ? value * relativeDoseInPercent
                                    : value * 100
                                ).toFixed(0)}{' '}
                                %
                            </OverlayLabel>
                            <Typography fontSize={10} lineHeight={1}>
                                {relativeToAbsoluteDose(value, dose?.maxDoseGy)}{' '}
                                Gy
                            </Typography>
                        </Box>
                    )}
                />
            </Box>
        )
    );
};

export default DoseColorwashSlider;
